const STUDENTS_KEY = "funshop.students.v1";
const ATTENDANCE_KEY = "funshop.attendance.v1";
const TEACHERS_KEY = "funshop.teachers.v1";
const ACTIVE_USER_KEY = "funshop.activeUser.v1";
const WORKSPACE_MODE_KEY = "funshop.workspaceMode.v1";
const SEEDED_FLAG = "funshop.seeded.v1";
const SETTINGS_KEY = "tempo.studioSettings.v1";
const LAST_STATUS_KEY = "tempo.lastStatus.v1";
const FEEDBACK_KEY = "tempo.feedback.v1";
const FEEDBACK_MODE_KEY = "tempo.feedbackMode.v1";

// Event names — UI subscribes to these to render "Last saved" / error states.
export const SAVED_EVENT = "tempo:saved";
export const SAVE_ERROR_EVENT = "tempo:save-error";

// In-session "last saved" timestamp; bumped on every successful write.
let lastSavedAt = null;

function emit(name, detail) {
    try {
        window.dispatchEvent(new CustomEvent(name, { detail }));
    } catch {
        // ignore if window/CustomEvent unavailable (SSR, tests).
    }
}

function read(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return fallback;
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
}

/**
 * Atomic-ish writer. localStorage.setItem itself is atomic at the JS level
 * (a key either gets the new string or nothing), so the only failure modes
 * are quota-exceeded or stringify errors. On either, we surface a save-error
 * event so the UI can warn the user — but we do NOT throw, because consumer
 * code shouldn't have to wrap every save call.
 *
 * Optimization: for empty arrays we `removeItem` instead of writing "[]".
 * Keeps localStorage clean after a Reset Studio and matches what `read`
 * treats as "no value".
 */
function write(key, value) {
    try {
        const isEmptyArray = Array.isArray(value) && value.length === 0;
        if (isEmptyArray) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, JSON.stringify(value));
        }
        lastSavedAt = new Date().toISOString();
        emit(SAVED_EVENT, { key, at: lastSavedAt });
        return true;
    } catch (err) {
        emit(SAVE_ERROR_EVENT, { key, error: err?.message || String(err) });
        return false;
    }
}

function readStr(key, fallback) {
    try {
        const v = localStorage.getItem(key);
        return v === null ? fallback : v;
    } catch {
        return fallback;
    }
}

function writeStr(key, value) {
    try {
        if (value === null || value === undefined || value === "") {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, String(value));
        }
        lastSavedAt = new Date().toISOString();
        emit(SAVED_EVENT, { key, at: lastSavedAt });
        return true;
    } catch (err) {
        emit(SAVE_ERROR_EVENT, { key, error: err?.message || String(err) });
        return false;
    }
}

/**
 * Single repository surface for app persistence. Currently backed by
 * localStorage; designed to be swapped wholesale for a real database without
 * touching consumers.
 *
 * TODO(supabase): replace each getter/setter in this object with a Supabase
 *   query (e.g. `supabase.from('students').select('*')`). Keep the function
 *   signatures and return shapes identical so the rest of the app keeps
 *   working unchanged.
 * TODO(rls): once Supabase is wired in, drop the manual `teacherId` filtering
 *   in DataContext — RLS will enforce it server-side based on auth.uid().
 * TODO(pwa-offline): add a service worker (Workbox or vite-plugin-pwa) that
 *   caches GET responses and queues mutations while offline; on reconnect,
 *   flush the queue through these same storage methods.
 */
export const storage = {
    /** Most recent successful write timestamp this session (ISO string). */
    getLastSavedAt() {
        return lastSavedAt;
    },
    // Students
    getStudents() {
        return read(STUDENTS_KEY, []);
    },
    saveStudents(students) {
        write(STUDENTS_KEY, students);
    },

    // Attendance
    getAttendance() {
        return read(ATTENDANCE_KEY, []);
    },
    saveAttendance(records) {
        write(ATTENDANCE_KEY, records);
    },

    // Teachers / Auth
    getTeachers() {
        return read(TEACHERS_KEY, []);
    },
    saveTeachers(teachers) {
        write(TEACHERS_KEY, teachers);
    },
    getActiveUserId() {
        return readStr(ACTIVE_USER_KEY, null);
    },
    setActiveUserId(id) {
        writeStr(ACTIVE_USER_KEY, id);
    },
    getWorkspaceMode() {
        return readStr(WORKSPACE_MODE_KEY, "all");
    },
    setWorkspaceMode(mode) {
        writeStr(WORKSPACE_MODE_KEY, mode);
    },

    // Seed flag
    isSeeded() {
        return localStorage.getItem(SEEDED_FLAG) === "1";
    },
    markSeeded() {
        localStorage.setItem(SEEDED_FLAG, "1");
    },

    // Studio settings (persisted as an object; see lib/settings.js for shape)
    getSettings() {
        return read(SETTINGS_KEY, null);
    },
    saveSettings(settings) {
        write(SETTINGS_KEY, settings);
    },

    // Sticky last-picked attendance status (single string).
    getLastStatus() {
        return readStr(LAST_STATUS_KEY, null);
    },
    setLastStatus(value) {
        writeStr(LAST_STATUS_KEY, value);
    },

    // Feedback (Operational Validation phase)
    getFeedback() {
        return read(FEEDBACK_KEY, []);
    },
    saveFeedback(items) {
        write(FEEDBACK_KEY, items);
    },
    isFeedbackModeOn() {
        return readStr(FEEDBACK_MODE_KEY, "0") === "1";
    },
    setFeedbackMode(on) {
        writeStr(FEEDBACK_MODE_KEY, on ? "1" : "");
    },

    /**
     * Atomic wipe of all studio data. Used by the "Reset studio" action in
     * Settings → Danger Zone. Does NOT preserve any keys; the user is sent
     * back through first-launch setup.
     */
    wipeAll() {
        try {
            [
                STUDENTS_KEY,
                ATTENDANCE_KEY,
                TEACHERS_KEY,
                ACTIVE_USER_KEY,
                WORKSPACE_MODE_KEY,
                SEEDED_FLAG,
                SETTINGS_KEY,
                LAST_STATUS_KEY,
                FEEDBACK_KEY,
                FEEDBACK_MODE_KEY,
            ].forEach((k) => localStorage.removeItem(k));
        } catch {
            // ignore
        }
    },

    /**
     * Pure JSON snapshot of every persisted key. Used by Settings → Backup
     * for a teacher to download a full export of the studio.
     */
    exportAll() {
        return {
            schema: "tempo.v1",
            exportedAt: new Date().toISOString(),
            students: read(STUDENTS_KEY, []),
            attendance: read(ATTENDANCE_KEY, []),
            teachers: read(TEACHERS_KEY, []),
            activeUserId: readStr(ACTIVE_USER_KEY, null),
            workspaceMode: readStr(WORKSPACE_MODE_KEY, "all"),
            settings: read(SETTINGS_KEY, null),
        };
    },

    /**
     * Replace all persisted state with the contents of an exported payload.
     * Throws if the payload doesn't look like a Tempo export.
     */
    importAll(payload) {
        if (!payload || payload.schema !== "tempo.v1") {
            throw new Error("This file is not a valid Tempo backup.");
        }
        if (!Array.isArray(payload.teachers) || !Array.isArray(payload.students)) {
            throw new Error("Backup is missing core data (teachers/students).");
        }
        write(STUDENTS_KEY, payload.students);
        write(ATTENDANCE_KEY, payload.attendance || []);
        write(TEACHERS_KEY, payload.teachers);
        writeStr(ACTIVE_USER_KEY, payload.activeUserId || null);
        writeStr(WORKSPACE_MODE_KEY, payload.workspaceMode || "all");
        write(SETTINGS_KEY, payload.settings || null);
    },
};

export function makeId(prefix = "id") {
    return `${prefix}_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
}
